/* eslint-disable no-unused-vars */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Box,
  BoxProps,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Flex,
  FlexProps,
  IconButton,
  useBreakpointValue,
} from '@chakra-ui/react';
import { SidebarSimple } from 'phosphor-react';

const SIDEBAR_COOKIE_NAME = 'sidebar_state';
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
export const SIDEBAR_WIDTH = '248px';
export const SIDEBAR_WIDTH_ICON = '62px';
const SIDEBAR_KEYBOARD_SHORTCUT = 'b';

interface SidebarContextValue {
  state: 'expanded' | 'collapsed';
  open: boolean;
  setOpen: (open: boolean) => void;
  openMobile: boolean;
  setOpenMobile: (open: boolean) => void;
  isMobile: boolean;
  toggleSidebar: () => void;
}

const SidebarContext = createContext<SidebarContextValue | null>(null);

export const useSidebar = (): SidebarContextValue => {
  const context = useContext(SidebarContext);
  if (!context) throw new Error('useSidebar must be used within a SidebarProvider.');
  return context;
};

const readCookieState = (): boolean | null => {
  const match = document.cookie.match(new RegExp(`${SIDEBAR_COOKIE_NAME}=(true|false)`));
  if (!match) return null;
  return match[1] === 'true';
};

export const SidebarProvider = ({
  defaultOpen = true,
  children,
}: {
  defaultOpen?: boolean;
  children: React.ReactNode;
}) => {
  const isMobile = useBreakpointValue({ base: true, md: false }, { fallback: 'md' }) ?? false;
  const [openMobile, setOpenMobile] = useState(false);
  const [open, setOpenState] = useState(() => readCookieState() ?? defaultOpen);

  const setOpen = useCallback((value: boolean | ((current: boolean) => boolean)) => {
    setOpenState((current) => {
      const next = typeof value === 'function' ? value(current) : value;
      document.cookie = `${SIDEBAR_COOKIE_NAME}=${next}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
      return next;
    });
  }, []);

  const toggleSidebar = useCallback(() => {
    if (isMobile) {
      setOpenMobile((current) => !current);
      return;
    }
    setOpen((current) => !current);
  }, [isMobile, setOpen]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === SIDEBAR_KEYBOARD_SHORTCUT && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        toggleSidebar();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleSidebar]);

  const value = useMemo<SidebarContextValue>(
    () => ({
      state: open ? 'expanded' : 'collapsed',
      open,
      setOpen,
      openMobile,
      setOpenMobile,
      isMobile,
      toggleSidebar,
    }),
    [open, setOpen, openMobile, isMobile, toggleSidebar]
  );

  return (
    <SidebarContext.Provider value={value}>
      <Flex minH="100vh" w="full" bg="surface.canvas">
        {children}
      </Flex>
    </SidebarContext.Provider>
  );
};

export const Sidebar = ({ children, ...rest }: BoxProps) => {
  const { isMobile, state, openMobile, setOpenMobile } = useSidebar();
  const width = state === 'expanded' ? SIDEBAR_WIDTH : SIDEBAR_WIDTH_ICON;

  if (isMobile) {
    return (
      <Drawer
        isOpen={openMobile}
        placement="left"
        autoFocus={false}
        onClose={() => setOpenMobile(false)}
      >
        <DrawerOverlay bg="rgba(0, 0, 0, 0.32)" backdropFilter="blur(4px)" />
        <DrawerContent maxW="288px" bg="surface.sidebar">
          <DrawerBody p={0}>
            <Flex direction="column" h="full">
              {children}
            </Flex>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <>
      <Box w={width} flexShrink={0} sx={{ transition: 'width 200ms linear' }} />
      <Flex
        as="aside"
        position="fixed"
        insetY={0}
        left={0}
        zIndex={10}
        w={width}
        h="100vh"
        direction="column"
        sx={{ transition: 'width 200ms linear' }}
        bg="surface.sidebar"
        {...rest}
      >
        {children}
      </Flex>
    </>
  );
};

export const SidebarHeader = ({ children, ...rest }: FlexProps) => {
  const { state } = useSidebar();
  return (
    <Flex
      direction="column"
      gap="8px"
      flexShrink={0}
      p={state === 'expanded' ? '12px' : '10px 8px 0'}
      {...rest}
    >
      {children}
    </Flex>
  );
};

export const SidebarContent = ({ children, ...rest }: FlexProps) => (
  <Flex
    direction="column"
    flex="1"
    minH={0}
    overflowY="auto"
    overflowX="hidden"
    sx={{
      '&::-webkit-scrollbar': { width: '0px' },
      scrollbarWidth: 'none',
    }}
    {...rest}
  >
    {children}
  </Flex>
);

export const SidebarFooter = ({ children, ...rest }: FlexProps) => {
  const { state } = useSidebar();
  return (
    <Flex
      direction="column"
      gap="8px"
      flexShrink={0}
      p={state === 'expanded' ? '12px' : '8px'}
      {...rest}
    >
      {children}
    </Flex>
  );
};

export const SidebarSeparator = (props: BoxProps) => {
  const { state } = useSidebar();
  if (state === 'collapsed') return null;
  return <Box mx="12px" h="1px" bg="line.hairline" flexShrink={0} {...props} />;
};

export const SidebarGroup = ({ children, ...rest }: FlexProps) => (
  <Flex direction="column" w="full" minW={0} px="12px" pt="16px" pb="0" {...rest}>
    {children}
  </Flex>
);

export const SidebarGroupLabel = ({ children, ...rest }: BoxProps) => {
  const { state } = useSidebar();
  if (state === 'collapsed') return null;
  return (
    <Box
      display="flex"
      alignItems="center"
      h="24px"
      mb="4px"
      px="8px"
      flexShrink={0}
      fontSize="12px"
      lineHeight="16px"
      color="content.faint"
      {...rest}
    >
      {children}
    </Box>
  );
};

export const SidebarGroupContent = ({ children, ...rest }: BoxProps) => (
  <Box w="full" fontSize="13px" {...rest}>
    {children}
  </Box>
);

export const SidebarTrigger = (props: any) => {
  const { toggleSidebar } = useSidebar();
  return (
    <IconButton
      aria-label="Alternar menu lateral"
      icon={<SidebarSimple size={16} />}
      size="xs"
      variant="ghost"
      color="content.subtle"
      flexShrink={0}
      onClick={toggleSidebar}
      {...props}
    />
  );
};

export const SidebarInset = ({ children, ...rest }: BoxProps) => (
  <Box
    as="main"
    position="relative"
    display="flex"
    flexDirection="column"
    flex="1"
    minW={0}
    h={{ base: '100vh', md: 'calc(100vh - 12px)' }}
    my={{ base: 0, md: '6px' }}
    mr={{ base: 0, md: '6px' }}
    overflow="hidden"
    bg="surface.base"
    borderWidth={{ base: '0', md: '1px' }}
    borderStyle="solid"
    borderColor="line.hairline"
    borderRadius={{ base: 0, md: 'xl' }}
    {...rest}
  >
    {children}
  </Box>
);
